import { VueGtag } from 'vue-gtag';
import { NetworkGraphEdges, NetworkGraphNodes } from '@/types/lab';
import { userSession } from '@/utils/userSession';

export const gaTracker = {
  getTrackingId: function () {
    const value = import.meta.env.VITE_GA_TRACKING_ID;
    return value;
  },
  config: function (gtag: VueGtag) {
    const userId = userSession.getUserId();
    const teamId = userSession.getTeamId();

    if (userId === null || teamId === null) return;

    gtag.config({
      custom_map: { dimension1: 'team_id' },
      team_id: teamId.toString(),
      page_location: window.location.href,
    });
    gtag.set({
      user_id: userId,
    });
  },
  clearConfig: function (gtag: VueGtag) {
    gtag.config({
      custom_map: { dimension1: 'team_id' },
      team_id: undefined,
    });
    gtag.set({
      user_id: undefined,
    });
  },
  /**
   * Labs ネットワークグラフ 画面を表示したか
   * @param gtag
   * @param graph_type グラフ種別 'organization_tag','member'
   */
  trackViewNetworkgraph: function (gtag: VueGtag, graph_type: string) {
    const trackingData: {
      graph_type: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type,
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };
    gtag.event('view_networkgraph', trackingData);
  },
  /**
   * Labs ネットワークグラフ グラフ描画
   * @param gtag
   * @param organization_tag_ids 組織タグID
   * @param contractId 契約ID : メンバーの場合のみ
   */
  trackDrawNetworkgraphForMember: function (
    gtag: VueGtag,
    organization_tag_ids: number[],
    contract_id: string[],
  ) {
    const trackingData: {
      graph_type: string;
      organization_tag_ids: number[];
      contract_id: string[];
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type: 'member',
      organization_tag_ids,
      contract_id,
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };

    gtag.event('draw_networkgraph', trackingData);
  },
  /**
   * Labs ネットワークグラフ グラフ描画
   * @param gtag
   * @param organization_tag_names 組織タグ
   * @param from_date 検索期間from
   * @param to_date 検索期間to
   */
  trackDrawNetworkgraphForOrganizationTag: function (
    gtag: VueGtag,
    organization_tag_names: string[],
    from_date: string,
    to_date: string,
  ) {
    const trackingData: {
      graph_type: string;
      organization_tag_names: string[];
      from_date: string;
      to_date: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type: 'organization_tag',
      organization_tag_names,
      from_date,
      to_date,
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };

    gtag.event('draw_networkgraph', trackingData);
  },
  /**
   * Labs ネットワークグラフ グラフノードのクリック
   * @param gtag
   * @param node_type ノードの種別
   * @param node_label ノードのラベル
   * @param user_id ユーザID
   */
  trackClickNetworkgraphNodeForMember: function (
    gtag: VueGtag,
    node_type: string,
    node_label: string,
    user_id?: number,
  ) {
    const trackingData: {
      graph_type: string;
      node_label: string;
      node_type: string;
      user_id?: number;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type: 'member',
      node_label,
      node_type,
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };
    if (user_id) {
      trackingData.user_id = user_id;
    }
    gtag.event('click_networkgraph_node', trackingData);
  },
  /**
   * Labs ネットワークグラフ グラフノードのクリック
   * @param gtag
   * @param node_type ノードの種別
   * @param node_label ノードのラベル
   */
  trackClickNetworkgraphNodeForOrganizationTag: function (
    gtag: VueGtag,
    node_type: string,
    node_label: string,
  ) {
    const trackingData: {
      graph_type: string;
      node_label: string;
      node_type: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type: 'organization_tag',
      node_label,
      node_type,
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };
    gtag.event('click_networkgraph_node', trackingData);
  },
  /**
   * Labs ネットワークグラフ グラフエッジのクリック
   * @param gtag
   * @param graph_type グラフ種別 'organization_tag','member'
   * @param node1 接続ノード1
   * @param node2 接続ノード2
   * @param relation_count ノード間のRelation数
   */
  trackClickNetworkgraphEdge: function (
    gtag: VueGtag,
    graph_type: string,
    node1: NetworkGraphNodes,
    node2: NetworkGraphNodes,
    relation_count: number,
  ) {
    const trackingData: {
      graph_type: string;
      node1_label: string;
      node2_label?: string;
      relation_count?: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type,
      node1_label: node1.label,
      node2_label: node2.label,
      relation_count: relation_count.toString(),
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };

    gtag.event('click_networkgraph_edge', trackingData);
  },
  /**
   * Labs ネットワークグラフ 右側インフォメーション欄でのユーザ情報参照
   * @param gtag
   * @param graph_type グラフ種別 'organization_tag','member'
   * @param source ソース情報
   * @param user_name ユーザ名
   * @param user_id ユーザID
   */
  trackClickNetworkInfoUser: function (
    gtag: VueGtag,
    graph_type: string,
    source: NetworkGraphNodes | NetworkGraphEdges | null,
    user_name: string,
    user_id: number,
  ) {
    const trackingData: {
      graph_type: string;
      source?: string;
      user_name: string;
      user_id: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type,
      user_name,
      user_id: user_id.toString(),
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };

    if (source === null) {
      trackingData['source'] = 'null';
    }
    // sourceがノードの場合
    else if ('type' in source) {
      trackingData['source'] = `node: ${source.label} ${source.type} `;
    } else {
      trackingData['source'] = 'edge';
    }

    gtag.event('click_networkgraph_info_user', trackingData);
  },
  /**
   * Labs ネットワークグラフ 右側インフォメーション欄での記事情報参照
   * @param gtag
   * @param graph_type グラフ種別 'organization_tag','member'
   * @param source ソース情報
   * @param article_id 記事ID
   */
  trackClickNetworkInfoArticle: function (
    gtag: VueGtag,
    graph_type: string,
    source: NetworkGraphNodes | NetworkGraphEdges | null,
    article_id: number,
  ) {
    const trackingData: {
      graph_type: string;
      article_id: string;
      source?: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type,
      article_id: article_id.toString(),
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };
    if (source === null) {
      trackingData['source'] = 'null';
    }
    // sourceがノードの場合
    else if ('type' in source) {
      trackingData['source'] = `node: ${source.label} ${source.type} `;
    } else {
      trackingData['source'] = 'edge';
    }

    gtag.event('click_networkgraph_info_article', trackingData);
  },
  trackClickNetworkInfoSearch: function (
    gtag: VueGtag,
    graph_type: string,
    source: NetworkGraphNodes | NetworkGraphEdges | null,
  ) {
    const trackingData: {
      graph_type: string;
      source?: string;
      tracking_location: string;
      hash: string;
      current_unix_time: number;
    } = {
      graph_type,
      tracking_location: window.location.href,
      hash: randomHash(),
      current_unix_time: currentUnixTime(),
    };

    if (source === null) {
      trackingData['source'] = 'null';
    }
    // sourceがノードの場合
    else if ('type' in source) {
      trackingData['source'] = `node: ${source.label} ${source.type} `;
    } else {
      trackingData['source'] = 'edge';
    }

    gtag.event('click_networkgraph_info_search', trackingData);
  },
  /**
   * Labs 注目トピック分析 画面を表示したか
   * @param gtag
   */
  trackViewTrendingTopics: function (gtag: VueGtag) {
    const trackingData: {
      tracking_location: string;
      current_unix_time: number;
    } = {
      tracking_location: window.location.href,
      current_unix_time: currentUnixTime(),
    };
    gtag.event('view_trending_topics', trackingData);
  },
};

const randomHash = () => {
  return Math.random().toString(36).slice(-8);
};

const currentUnixTime = () => {
  return new Date().getTime();
};
